/*
  Register handlers: the TSA schedule, planned hires, and the legal-entity registry.

  Mostly CRUD, with three places where the original's behaviour is load-bearing:

  1. Deleting a build-up must also drop their bucket, their stage metadata and any head designation
     they hold (removeHeadEverywhere, orgchart.html:2646). A planned hire who heads a team and is then
     deleted otherwise leaves a designation pointing at somebody who no longer exists.

  2. Renaming a legal entity has to cascade into per-scenario overrides and group renames. The original
     does this for the ACTIVE scenario only (:8269), so every other scenario keeps the old name and
     silently stops matching. Fixed here, via a write that spans the project.

  3. An entity remap is not a registry edit at all — it is a per-scenario, per-perspective group rename
     on the legalEntity field (:8259), including the detail that remapping something back onto itself
     REMOVES the mapping rather than storing an identity.
*/
import { DEFAULT_ENTITY_CLASSIFICATION } from '@integrate/contracts';
import { tsaReplacementSide } from '../economics.js';
import { CommandError } from '../errors.js';
import { personOf } from './decisions.js';
import { required } from './types.js';
/* ------------------------------------------------------------------ *
 * TSAs
 * ------------------------------------------------------------------ */
export const addTsa = {
    needs: ['tsas'],
    apply(slice, command) {
        const tsas = required(slice, 'tsas');
        if (tsas[command.tsa.id]) {
            throw new CommandError('conflict', `A TSA with id ${command.tsa.id} already exists`);
        }
        return {
            writes: [
                {
                    t: 'tsa',
                    scenarioId: command.scenarioId,
                    id: command.tsa.id,
                    row: command.tsa,
                    // Absent on an ordinary add, so the row takes the clock and lands at the end. Present only
                    // when this IS the inverse of a delete, which is what puts it back where it was.
                    createdAt: command.restoredAt,
                },
            ],
            inverse: [{ type: 'deleteTsa', scenarioId: command.scenarioId, tsaId: command.tsa.id }],
            summary: `Added TSA "${command.tsa.name}"`,
            touched: [`tsa:${command.tsa.id}`],
        };
    },
};
export const updateTsa = {
    needs: ['tsas'],
    apply(slice, command) {
        const existing = required(slice, 'tsas')[command.tsaId];
        if (!existing)
            throw new CommandError('not_found', `No such TSA: ${command.tsaId}`);
        const next = { ...existing, ...command.patch };
        // The inverse names only the fields this command touched, so undoing an edit to the monthly fee
        // does not also reassert a name somebody else changed in between.
        const before = {};
        for (const key of Object.keys(command.patch)) {
            before[key] = existing[key];
        }
        const changed = Object.keys(command.patch)
            .filter((k) => existing[k] !== next[k])
            .join(', ');
        return {
            writes: [{ t: 'tsa', scenarioId: command.scenarioId, id: command.tsaId, row: next }],
            inverse: [
                { type: 'updateTsa', scenarioId: command.scenarioId, tsaId: command.tsaId, patch: before },
            ],
            summary: changed
                ? `TSA "${existing.name}": updated ${changed}`
                : `TSA "${existing.name}" unchanged`,
            touched: [`tsa:${command.tsaId}`],
        };
    },
};
export const deleteTsa = {
    needs: ['tsas', 'buildUps', 'registerCreatedAt'],
    apply(slice, command) {
        const existing = required(slice, 'tsas')[command.tsaId];
        if (!existing)
            throw new CommandError('not_found', `No such TSA: ${command.tsaId}`);
        // A replacement hire exists to take over a specific service, so a dangling link would leave the
        // TSA economics counting a replacement for nothing. The database nulls it; say so plainly.
        const orphaned = Object.values(required(slice, 'buildUps')).filter((b) => b.tsaId === command.tsaId);
        /*
          The orphaned links are written explicitly rather than left to the database.
    
          The foreign key nulls them either way, but a side effect the command does not name cannot be
          undone: re-adding the TSA alone brought it back with every replacement unmatched — 0 of N matched,
          the economics counting no replacement run-rate — and nothing said so. It also meant a core-only
          consumer of applyCommand saw dangling links the server never would, which is the kind of gap that
          only shows up somewhere far away.
        */
        return {
            writes: [
                { t: 'tsa', scenarioId: command.scenarioId, id: command.tsaId, row: null },
                ...orphaned.map((b) => ({
                    t: 'buildUp',
                    scenarioId: command.scenarioId,
                    id: b.id,
                    row: { ...b, tsaId: null },
                })),
            ],
            inverse: [
                /*
                  THE ROW'S OWN CLOCK GOES WITH IT.
        
                  The register is ordered by `(created_at, id)`, and a re-insert takes a fresh `now()` — so
                  undoing a delete used to put the agreement back at the BOTTOM of a list somebody reads down.
                  Read plainly that is an inverse restoring every field of a row except one, which is the defect
                  this repo has now shipped three times in other shapes.
        
                  Undefined when the loader did not carry it, which is a real case rather than a defensive one:
                  a core-only consumer running handlers without a database has no clock to give. Absent then
                  means "wherever it lands", which is the behaviour that was there before.
                */
                {
                    type: 'addTsa',
                    scenarioId: command.scenarioId,
                    tsa: existing,
                    restoredAt: required(slice, 'registerCreatedAt')[`tsa:${command.tsaId}`],
                },
                ...orphaned.map((b) => ({
                    type: 'updateBuildUp',
                    scenarioId: command.scenarioId,
                    buildUpId: b.id,
                    patch: { tsaId: existing.id },
                })),
            ],
            summary: `Removed TSA "${existing.name}"` +
                (orphaned.length
                    ? ` — ${orphaned.length} replacement ${orphaned.length === 1 ? 'hire' : 'hires'} no longer replaces anything`
                    : ''),
            touched: [`tsa:${command.tsaId}`, ...orphaned.map((b) => `buildUp:${b.id}`)],
        };
    },
};
/* ------------------------------------------------------------------ *
 * Build-ups
 * ------------------------------------------------------------------ */
/**
 * A supervisor has to be somebody who exists, and has to be a real one.
 *
 * The original's picker offers `state.employees` only (orgchart.html:7139), so a planned hire cannot
 * report to another planned hire. That is not arbitrary: build-ups are scenario-scoped and re-keyed on
 * every clone, so a chain of them would break at the first duplicate, and there is nobody in post to
 * report to anyway. An id nobody holds is refused rather than stored, because the reporting layout
 * silently reparents a dangling supervisor to the top and nothing on screen says why.
 */
function assertSupervisor(slice, supervisorId) {
    if (supervisorId === null)
        return;
    if (required(slice, 'employees')[supervisorId])
        return;
    if (slice.buildUps?.[supervisorId]) {
        throw new CommandError('conflict', 'A planned hire cannot report to another planned hire — pick somebody already in post');
    }
    throw new CommandError('not_found', `No such supervisor: ${supervisorId}`);
}
/**
 * The fields that only mean something together, reconciled the way the original does.
 *
 * Two couplings, and only one of them is about the wave alone.
 *
 * A Day 2 hire arrives after separation, so it cannot also be a Day 2 exit — that is a position cut at
 * the moment it is filled. `wireStageSection` deletes `day2Exit` when the wave becomes Day 2
 * (orgchart.html:5126), and that holds for any hire.
 *
 * The other direction is NOT "no Day 1 row may carry a TSA link", which is what this used to say and
 * what made every RemainCo internalisation hire unrecordable. The original clears the link on the way
 * back to Day 1 only inside the CARVE-side stage section (`stageSectionHtml` renders it for Carve-out
 * alone, `:5327`); a Remain-side hire gets `remainTsaSectionHtml` (`:5042`), which has no wave toggle
 * and never clears anything. And `saveBuildUp` deliberately writes `wave: 'day1'` together with a
 * `tsaId` for a provided TSA (`:7211`, `:7229`) — a reverse agreement RemainCo internalises when it
 * ends, whose replacement is by construction a Day 1 Remain position.
 *
 * So the real rule is about the LINK's side: a received TSA's replacement is a carve-out Day 2 hire, so
 * a Day 1 row holding a received link is the contradiction. A Day 1 row holding a provided link is the
 * ordinary case, and `computeTsaEconomics` reads exactly that shape (`remainRepl` filters on `tsaId`
 * and the Remain bucket, never on the wave).
 */
function reconcileWave(row, tsas) {
    if (row.wave === 'day2')
        return { ...row, day2Exit: false };
    const linked = row.tsaId ? tsas[row.tsaId] : undefined;
    if (linked && tsaReplacementSide(linked) === 'Carve-out') {
        return { ...row, tsaId: null, neededBy: null };
    }
    return row;
}
/** Which fields `reconcileWave` may rewrite — every one has to be in the inverse's patch. */
const WAVE_COUPLED = ['wave', 'tsaId', 'neededBy', 'day2Exit'];
export const addBuildUp = {
    needs: ['buildUps', 'tsas', 'employees'],
    apply(slice, command) {
        const buildUps = required(slice, 'buildUps');
        if (buildUps[command.buildUp.id]) {
            throw new CommandError('conflict', `A build-up with id ${command.buildUp.id} already exists`);
        }
        if (command.buildUp.tsaId && !required(slice, 'tsas')[command.buildUp.tsaId]) {
            throw new CommandError('not_found', `No such TSA to replace: ${command.buildUp.tsaId}`);
        }
        assertSupervisor(slice, command.buildUp.supervisorId);
        /*
          A position nobody has sized is a whole one.
    
          The app's form defaults FTE to 1 and coerces a zero back to 1 (`Number(...) || 1`, `:7217`), and
          the React form repeats it — but the rule belonged here, not in a component. `fte` is nullable
          through the schema, the column and the handler, while headcount counts every hire as 1 and the
          FTE metric reads `Number(p.fte) || 0`: a null makes a position that exists for headcount and does
          not exist for FTE. Harmless while one form is the only writer; not harmless once P6 generates a
          tool definition from this schema, where an unspecified number arrives as null.
    
          The default lives on the CREATE path only. `buildUpsFromMeta` deliberately does not do this: the
          app back-fills no FTE on load, so coercing an imported blank to 1 would make the platform's FTE
          totals disagree with the app's for the same file.
        */
        const row = reconcileWave({ ...command.buildUp, fte: command.buildUp.fte || 1 }, required(slice, 'tsas'));
        return {
            writes: [
                // See `addTsa`: absent on an ordinary add, present only when this is the inverse of a delete.
                { t: 'buildUp', scenarioId: command.scenarioId, id: row.id, row, createdAt: command.restoredAt },
            ],
            inverse: [
                { type: 'deleteBuildUp', scenarioId: command.scenarioId, buildUpId: command.buildUp.id },
            ],
            summary: `Added build-up "${row.name}" (${row.wave === 'day2' ? 'Day 2' : 'Day 1'})`,
            touched: [`buildUp:${command.buildUp.id}`],
        };
    },
};
export const updateBuildUp = {
    needs: ['buildUps', 'tsas', 'employees'],
    apply(slice, command) {
        const existing = required(slice, 'buildUps')[command.buildUpId];
        if (!existing)
            throw new CommandError('not_found', `No such build-up: ${command.buildUpId}`);
        if (command.patch.tsaId && !required(slice, 'tsas')[command.patch.tsaId]) {
            throw new CommandError('not_found', `No such TSA to replace: ${command.patch.tsaId}`);
        }
        if (command.patch.supervisorId !== undefined) {
            assertSupervisor(slice, command.patch.supervisorId);
        }
        const next = reconcileWave({ ...existing, ...command.patch }, required(slice, 'tsas'));
        /*
          The inverse restores every field the patch named PLUS every field the reconciliation may have
          rewritten — otherwise switching a Day 2 replacement back to Day 1 stores an inverse of
          `{wave:'day2'}` alone, and undo brings the wave back without the TSA link or the needed-by month
          it silently dropped on the way through.
        */
        const before = {};
        const keys = new Set([...Object.keys(command.patch), ...WAVE_COUPLED]);
        for (const key of keys) {
            before[key] = existing[key];
        }
        return {
            writes: [{ t: 'buildUp', scenarioId: command.scenarioId, id: command.buildUpId, row: next }],
            inverse: [
                {
                    type: 'updateBuildUp',
                    scenarioId: command.scenarioId,
                    buildUpId: command.buildUpId,
                    patch: before,
                },
            ],
            summary: `Build-up "${existing.name}": updated ${Object.keys(command.patch).join(', ')}`,
            touched: [`buildUp:${command.buildUpId}`],
        };
    },
};
export const deleteBuildUp = {
    needs: ['buildUps', 'decisions', 'heads', 'registerCreatedAt'],
    apply(slice, command) {
        const existing = required(slice, 'buildUps')[command.buildUpId];
        if (!existing)
            throw new CommandError('not_found', `No such build-up: ${command.buildUpId}`);
        const writes = [
            { t: 'buildUp', scenarioId: command.scenarioId, id: command.buildUpId, row: null },
            // A build-up carries a bucket like anybody else; deleting the person must not leave it behind.
            { t: 'decision', scenarioId: command.scenarioId, employeeId: command.buildUpId, patch: null },
        ];
        // And any group they head. The original calls removeHeadEverywhere for exactly this, scoped to the
        // scenario — correctly, since a build-up only exists inside one.
        const heads = required(slice, 'heads');
        const restoreHeads = [];
        for (const [perspective, groups] of Object.entries(heads)) {
            for (const [groupId, employeeId] of Object.entries(groups)) {
                if (employeeId !== command.buildUpId)
                    continue;
                writes.push({
                    t: 'head',
                    scenarioId: command.scenarioId,
                    perspective: perspective,
                    groupId,
                    employeeId: null,
                    present: false,
                });
                restoreHeads.push({
                    type: 'setHead',
                    scenarioId: command.scenarioId,
                    perspective: perspective,
                    groupId,
                    employeeId: command.buildUpId,
                });
            }
        }
        const previousBucket = required(slice, 'decisions')[command.buildUpId];
        return {
            writes,
            inverse: [
                // The row's own clock goes back with it; see `deleteTsa` for why that is a restored FIELD
                // rather than an ordering feature.
                {
                    type: 'addBuildUp',
                    scenarioId: command.scenarioId,
                    buildUp: existing,
                    restoredAt: required(slice, 'registerCreatedAt')[`buildUp:${command.buildUpId}`],
                },
                ...(previousBucket?.status
                    ? [
                        {
                            type: 'setBucket',
                            scenarioId: command.scenarioId,
                            employeeId: command.buildUpId,
                            bucket: previousBucket.status,
                            ...(previousBucket.joinStage ? { joinStage: previousBucket.joinStage } : {}),
                            // No day2Exit: a hire's is on the build-up row, which `addBuildUp` above restores in
                            // full. Asserting it here would put back a decision-row flag no reader honours — the
                            // same stray value `setDay2Exit` now refuses to write in the first place.
                        },
                    ]
                    : []),
                ...restoreHeads,
            ],
            summary: `Removed build-up "${existing.name}"` +
                (restoreHeads.length ? ` and their ${restoreHeads.length} head designation(s)` : ''),
            touched: [`buildUp:${command.buildUpId}`, `bucket:${command.buildUpId}`],
        };
    },
};
/* ------------------------------------------------------------------ *
 * Legal entities
 * ------------------------------------------------------------------ */
export const addEntity = {
    needs: ['entities'],
    apply(slice, command) {
        const entities = required(slice, 'entities');
        if (entities[command.entityId]) {
            throw new CommandError('conflict', `An entity with id ${command.entityId} already exists`);
        }
        const clash = Object.values(entities).find((e) => e.name.trim().toLowerCase() === command.name.trim().toLowerCase());
        if (clash)
            throw new CommandError('conflict', `An entity named "${command.name}" already exists`);
        return {
            writes: [
                {
                    t: 'entity',
                    id: command.entityId,
                    row: {
                        name: command.name,
                        country: command.country,
                        classification: command.classification,
                    },
                },
            ],
            inverse: [{ type: 'deleteEntity', entityId: command.entityId }],
            summary: `Added legal entity "${command.name}" (${command.classification})`,
            touched: [`entity:${command.entityId}`],
        };
    },
};
export const updateEntity = {
    needs: ['entities'],
    apply(slice, command) {
        const entities = required(slice, 'entities');
        const existing = entities[command.entityId];
        if (!existing)
            throw new CommandError('not_found', `No such entity: ${command.entityId}`);
        /*
          A RENAME MAY NOT COLLIDE, which `addEntity` has always enforced and this did not.
    
          The asymmetry mattered because `entityClassOf` resolves BY NAME: two registry rows called
          "NewCo GmbH" mean every lookup returns whichever happens to come first, so one of the two rows
          silently governs the other's people. Renaming onto an existing name looks like a merge and is
          not one — nothing combines the rows, and `entityRenameEverywhere` below would rewrite the
          mappings of both.
    
          The frozen app blocks this in its click handler (orgchart.html:8397). A rule enforced only in
          one client is a rule the API does not have, so it is enforced here, where every caller —
          the panel, a proposal, the assistant — meets it.
        */
        if (command.patch.name !== undefined) {
            const wanted = command.patch.name.trim().toLowerCase();
            const clash = Object.entries(entities).find(([id, e]) => id !== command.entityId && e.name.trim().toLowerCase() === wanted);
            if (clash) {
                throw new CommandError('conflict', `An entity named "${command.patch.name}" already exists`);
            }
        }
        const next = { ...existing, ...command.patch };
        const writes = [
            {
                t: 'entity',
                id: command.entityId,
                row: { name: next.name, country: next.country, classification: next.classification },
            },
        ];
        // A rename has to follow the name everywhere it was used as a value: per-scenario entity
        // mappings and per-person overrides both key on the name, not the id. The original does this for
        // the active scenario only, so every other scenario ends up pointing at a name that is gone.
        const renamed = command.patch.name !== undefined && command.patch.name !== existing.name;
        if (renamed) {
            writes.push({
                t: 'entityRenameEverywhere',
                oldName: existing.name,
                newName: command.patch.name,
            });
        }
        const before = {};
        for (const key of Object.keys(command.patch)) {
            before[key] = existing[key];
        }
        const changes = Object.entries(command.patch)
            .filter(([k, v]) => existing[k] !== v)
            .map(([k, v]) => `${k} → ${v ?? '(cleared)'}`)
            .join(', ');
        return {
            writes,
            inverse: [{ type: 'updateEntity', entityId: command.entityId, patch: before }],
            summary: changes
                ? `Entity "${existing.name}": ${changes}`
                : `Entity "${existing.name}" unchanged`,
            touched: [`entity:${command.entityId}`],
        };
    },
};
export const deleteEntity = {
    needs: ['entities'],
    apply(slice, command) {
        const existing = required(slice, 'entities')[command.entityId];
        if (!existing)
            throw new CommandError('not_found', `No such entity: ${command.entityId}`);
        return {
            // Only the registry entry goes. People keep their legalEntity value, and an unregistered entity
            // reads as the default classification — which is what the original does, and is recoverable.
            writes: [{ t: 'entity', id: command.entityId, row: null }],
            inverse: [
                {
                    type: 'addEntity',
                    entityId: command.entityId,
                    name: existing.name,
                    country: existing.country,
                    classification: existing.classification,
                },
            ],
            summary: `Removed legal entity "${existing.name}" from the registry`,
            touched: [`entity:${command.entityId}`],
        };
    },
};
export const remapEntity = {
    needs: ['employees', 'groupRenames'],
    apply(slice, command) {
        for (const employeeId of command.employeeIds)
            personOf(slice, employeeId);
        const renames = required(slice, 'groupRenames')[command.perspective] ?? {};
        const forField = renames.legalEntity ?? {};
        const previous = Object.prototype.hasOwnProperty.call(forField, command.fromEntity)
            ? forField[command.fromEntity]
            : null;
        // Remapping something onto itself is a REMOVAL, not an identity mapping — the original deletes
        // the key (orgchart.html:8264), and a stored self-map would make the overrides-active indicator
        // claim a transfer that is not there.
        const isRemoval = command.toEntity === command.fromEntity || command.toEntity.trim() === '';
        const n = command.employeeIds.length;
        return {
            writes: [
                {
                    t: 'groupRename',
                    scenarioId: command.scenarioId,
                    perspective: command.perspective,
                    fieldKey: 'legalEntity',
                    oldValue: command.fromEntity,
                    newValue: isRemoval ? null : command.toEntity,
                },
            ],
            inverse: [
                {
                    type: 'remapEntity',
                    scenarioId: command.scenarioId,
                    perspective: command.perspective,
                    fromEntity: command.fromEntity,
                    toEntity: previous ?? command.fromEntity,
                    employeeIds: command.employeeIds,
                },
            ],
            summary: isRemoval
                ? `Cancelled the entity transfer out of "${command.fromEntity}" (${n} ${n === 1 ? 'person' : 'people'})`
                : `${command.fromEntity} → ${command.toEntity} (${n} ${n === 1 ? 'person' : 'people'}) [${command.perspective}]`,
            touched: [`entityMap:${command.perspective}:${command.fromEntity}`],
        };
    },
};
/** Exported so the importer can seed a registry the same way the app does. */
export const SEEDED_CLASSIFICATION = DEFAULT_ENTITY_CLASSIFICATION;
//# sourceMappingURL=registers.js.map